import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { IcStatistics } from '@assets/icons/24x24';
import { Center, For, HStack, Icon, IconButton, Spinner, Text, VStack } from '@chakra-ui/react';
import NotificationCard from '@components/cockpit/panels/notification/card/NotificationCard';
import { NotificationSearchExportButton } from '@components/cockpit/panels/notificationSearch/export/NotificationSearchExportButton';
import { useNotificationSearchContext } from '@components/cockpit/panels/notificationSearch/NotificationSearchContext';
import { DEFAULT_PAGE_SIZE } from '@components/cockpit/panels/notificationSearch/NotificationSearchPanel';
import CustomChipList from '@components/common/inputs/CustomChipList';
import { ScrollArea } from '@components/ui/ScrollArea';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { stringToOption } from '@utils/components/customChipList.utils';
import { FormattedMessage } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { NotificationFamilyEnum, NotificationSortEnum } from '@/types/data/data.types';

import { Pagination } from '../../../ui/Pagination';
import NotificationDetails from '../notification/detail/NotificationDetails';
import { StatisticsActionButton } from './StatisticsActionButton';

export function NotificationSearchList() {
  const {
    criteria,
    notificationsTotal,
    notifications,
    isFetching,
    filters,
    setFilters,
    selectedNotification,
    setSelectedNotification,
    openedReporting,
    setOpenedReporting,
    page,
    setPage,
  } = useNotificationSearchContext();

  const canExport = useAppSelector((state) => hasRole(state, RoleEnum.EXPORT_NOTIFICATIONS));

  return (
    <HStack height="100%" gap={0.25} backgroundColor="neutral.black" minWidth={0} flex={1}>
      {notifications.length === 0 ? (
        <Center height="100%" width="100%" backgroundColor="neutral.black">
          {isFetching ? (
            <Spinner color="neutral.200" width="100px" height="100px" />
          ) : (
            <VStack gap={2} paddingTop="58px">
              <Icon boxSize="194px" as={EmptyPanelDroneIcon} />
              <Text color="neutral.200" textAlign="center" userSelect="none">
                <FormattedMessage id="components.notificationSearch.empty" />
              </Text>
            </VStack>
          )}
        </Center>
      ) : (
        <>
          <VStack height="100%" minWidth={0} flex={1} gap={0.25} backgroundColor="neutral.900">
            <HStack width="100%" height="56px" backgroundColor="neutral.800" flexShrink={0} gap={0} paddingLeft={2}>
              <Pagination
                onPreviousPage={() => setPage(Math.max(page - 1, 0))}
                onNextPage={() => setPage(page + 1)}
                currentPage={page}
                pageSize={criteria.size ?? DEFAULT_PAGE_SIZE}
                total={notificationsTotal ?? 0}
              />
              <HStack gap={1} marginLeft="auto" marginRight={2}>
                <Text color="neutral.300">
                  <FormattedMessage id="components.panel.sort" />
                </Text>
                <CustomChipList
                  availableOptions={Object.values(NotificationSortEnum).map(stringToOption)}
                  selectedOptions={filters.sort ? [stringToOption(filters.sort)] : []}
                  path="components.notification.sort"
                  isDisabled
                  distinctValues={Object.values(NotificationSortEnum)}
                  canUnselectDistinct
                  onChange={(value: string[]) => setFilters({ sort: value[0] as NotificationSortEnum })}
                />
              </HStack>
              <StatisticsActionButton />
              {filters.category === NotificationFamilyEnum.LAD && (
                <IconButton
                  aria-label="Open reporting"
                  variant="cockpitHeader"
                  boxSize="56px"
                  data-active={openedReporting}
                  onClick={() => setOpenedReporting(true)}
                >
                  <Icon as={IcStatistics} boxSize="24px" />
                </IconButton>
              )}
              {canExport && filters.category === NotificationFamilyEnum.LAD && <NotificationSearchExportButton />}
            </HStack>
            <ScrollArea>
              <VStack gap={0} width="100%">
                <For each={notifications}>
                  {(notification) => (
                    <NotificationCard
                      key={notification.id}
                      isArchived
                      isSearch
                      isSelected={selectedNotification === notification.id}
                      setSelectedNotification={setSelectedNotification}
                      archivedNotification={notification}
                    />
                  )}
                </For>
              </VStack>
            </ScrollArea>
          </VStack>
          <NotificationDetails selectedNotification={selectedNotification} isArchived isSearch />
        </>
      )}
    </HStack>
  );
}
